import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import soccer from '../icon/icons/soccer-ball.svg';
import voleyball from '../icon/icons/voleyball-ball.svg';
import basketball from '../icon/icons/basketball-ball.svg';

const loaderAnimation = keyframes`
  from {
    background-image: url(${soccer});
    transform: translateY(0);
  }
  12.5%{
    background-image: url(${soccer});
    transform: translateY(-4px);
  }
  25%{
    background-image: url(${soccer});
    transform: translateY(0);
  }
  37.5% {
    background-image: url(${voleyball});
    transform: translateY(0);
  }
  50%{
    background-image: url(${voleyball});
    transform: translateY(-4px);
  }
  62.5%{
    background-image: url(${voleyball});
    transform: translateY(0);
  }
  75% {
    background-image: url(${basketball});
    transform: translateY(0);
  }
  87.5%{
    background-image: url(${basketball});
    transform: translateY(-4px);
  }
  to{
    background-image: url(${basketball});
    transform: translateY(0);
  }
`;

export const Button = styled.button`
  padding: 8px 16px;
  box-sizing: border-box;
  border-radius: 8px;
  outline-width: 1px;
  outline-style: solid;
  border-width: 1px;
  border-style: solid;
  cursor: pointer;
  user-select: none;
  line-height: unset;
  outline-color: transparent;
  border-color: transparent;

  &.loading {
    position: relative;
    user-select: none;
    pointer-events: none;
    &::before {
      position: absolute;
      top: 0;
      left: 0;
      content: '';
      display: block;
      width: 100%;
      height: 100%;
      background-color: inherit;
      border-radius: inherit;
    }
    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      content: '';
      display: block;
      width: 14px;
      height: 14px;
      margin-top: -7px;
      margin-left: -7px;
      background-size: 14px 14px;
      animation: ${loaderAnimation} 3s ease infinite alternate;
    }
  }

  &.slim {
    padding: 4px 8px;
    border-radius: 4px;
  }

  &[disabled] {
    cursor: default;
    pointer-events: none;
  }

  &.primary {
    background-color: ${({ theme }) => theme.colors.AccentNapplesYellow};
    outline-color: ${({ theme }) => theme.colors.AccentNapplesYellow};
    border-color: ${({ theme }) => theme.colors.AccentNapplesYellow};
    color: ${({ theme }) => theme.colors.SurfaceRaisinBlack50};

    &:focus {
      border-color: ${({ theme }) => theme.colors.SurfaceWhite};
      outline-color: ${({ theme }) => theme.colors.AccentJonquil};
    }
    &:hover {
      background-color: ${({ theme }) => theme.colors.AccentJonquil};
      border-color: ${({ theme }) => theme.colors.AccentJonquil};
      outline-color: ${({ theme }) => theme.colors.AccentJonquil};
    }
    &:active {
      background: radial-gradient(
          13.84% 40.61% at 50% 50%,
          ${({ theme }) => theme.colors.AccentJonquil} 99.99%,
          rgba(242, 202, 24, 0) 100%
        ),
        ${({ theme }) => theme.colors.AccentNapplesYellow};
      border-color: ${({ theme }) => theme.colors.AccentNapplesYellow};
      outline-color: ${({ theme }) => theme.colors.AccentNapplesYellow};
    }
    &[disabled] {
      background: ${({ theme }) => theme.colors.AccentMediumChampagne};
      outline-color: ${({ theme }) => theme.colors.AccentMediumChampagne};
      border-color: ${({ theme }) => theme.colors.AccentMediumChampagne};
      color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
    }
  }

  &.secondary {
    background-color: #FFFFFF;
    outline-color: ${({ theme }) => theme.colors.AccentNapplesYellow};
    border-color: transparent;
    color: ${({ theme }) => theme.colors.SurfaceRaisinBlack50};

    &:focus {
      position: relative;
      background-color: ${({ theme }) => theme.colors.AccentCornsilk};
      border-color: ${({ theme }) => theme.colors.SurfaceWhite};
      outline-color: ${({ theme }) => theme.colors.AccentJonquil};
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        border-radius: inherit;
        width: calc(100% - 2px);
        height: calc(100% - 2px);
        background-color: transparent;
        border: 1px solid ${({ theme }) => theme.colors.AccentJonquil};
      }
    }
    &:hover {
      background-color: ${({ theme }) => theme.colors.AccentCornsilk};
      outline-color: ${({ theme }) => theme.colors.AccentJonquil};
      border-color: ${({ theme }) => theme.colors.AccentCornsilk};
    }
    &:active {
      background: radial-gradient(
          13.84% 40.61% at 50% 50%,
          ${({ theme }) => theme.colors.AccentCornsilk} 99.99%,
          rgba(242, 202, 24, 0) 100%
        ),
        ${({ theme }) => theme.colors.SurfaceWhite};
      border-color: ${({ theme }) => theme.colors.SurfaceWhite};
      &::after {
        display: none;
      }
    }
    &[disabled] {
      background: ${({ theme }) => theme.colors.AccentMediumChampagne};
      outline-color: ${({ theme }) => theme.colors.AccentMediumChampagne};
      border-color: ${({ theme }) => theme.colors.SurfaceWhite};
      color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
    }
  }

  &.dark {
    background-color: ${({ theme }) => theme.colors.SurfaceIndependence40};
    outline-color: ${({ theme }) => theme.colors.SurfaceIndependence40};
    border-color: ${({ theme }) => theme.colors.SurfaceIndependence40};
    color: ${({ theme }) => theme.colors.SurfaceWhite};

    &:focus {
      border-color: ${({ theme }) => theme.colors.SurfaceWhite};
      outline-color: ${({ theme }) => theme.colors.AccentJonquil};
      color: ${({ theme }) => theme.colors.AccentNapplesYellow};
    }
    &:hover {
      color: ${({ theme }) => theme.colors.AccentNapplesYellow};
    }
    &:active {
      outline-color: ${({ theme }) => theme.colors.SurfaceIndependence40};
      border-color: ${({ theme }) => theme.colors.SurfaceIndependence40};
      color: ${({ theme }) => theme.colors.AccentNapplesYellow};
    }
    &[disabled] {
      color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
    }
    &.loading {
      &::after {
        filter: invert(1) saturate(5) hue-rotate(351deg);
      }
    }
  }

  &.light {
    background-color: ${({ theme }) => theme.colors.SurfaceWhite};
    outline-color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
    border-color: ${({ theme }) => theme.colors.SurfaceWhite};
    color: ${({ theme }) => theme.colors.SurfaceRaisinBlack50};

    &:focus {
      position: relative;
      background-color: ${({ theme }) => theme.colors.AccentCornsilk};
      border-color: ${({ theme }) => theme.colors.SurfaceWhite};
      outline-color: ${({ theme }) => theme.colors.AccentNapplesYellow};
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        border-radius: inherit;
        width: calc(100% - 2px);
        height: calc(100% - 2px);
        background-color: transparent;
        border: 1px solid ${({ theme }) => theme.colors.AccentJonquil};
      }
    }
    &:hover {
      background-color: ${({ theme }) => theme.colors.AccentCornsilk};
      border-color: ${({ theme }) => theme.colors.AccentCornsilk};
      outline-color: ${({ theme }) => theme.colors.AccentNapplesYellow};
    }
    &:active {
      background-color: ${({ theme }) => theme.colors.AccentNapplesYellow};
      border-color: ${({ theme }) => theme.colors.AccentNapplesYellow};
      outline-color: ${({ theme }) => theme.colors.AccentNapplesYellow};
      &::after {
        display: none;
      }
    }
    &[disabled] {
      outline-color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
      color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
    }
  }

  &.tertiary {
    background-color: transparent;
    outline-color: transparent;
    border-color: transparent;
    color: ${({ theme }) => theme.colors.AccentNapplesYellow};

    &:focus {
      border-color: transparent;
      outline-color: ${({ theme }) => theme.colors.AccentJonquil};
      border-color: transparent;
    }
    &:hover {
      background-color: ${({ theme }) => theme.colors.AccentCornsilk};
      border-color: ${({ theme }) => theme.colors.AccentCornsilk};
      outline-color: ${({ theme }) => theme.colors.AccentCornsilk};
      color: ${({ theme }) => theme.colors.AccentJonquil};
    }
    &.for-dark:hover {
      background-color: transparent;
      outline-color: transparent;
      border-color: transparent;
      color: ${({ theme }) => theme.colors.AccentNapplesYellow};
    }
    &:active {
      border-color: transparent;
      outline-color: ${({ theme }) => theme.colors.AccentCornsilk};
      color: ${({ theme }) => theme.colors.AccentJonquil};
      background: radial-gradient(
          13.84% 40.61% at 50% 50%,
          ${({ theme }) => theme.colors.AccentCornsilk} 99.99%,
          rgba(242, 202, 24, 0) 100%
        ),
        transparent;
    }
    &.for-dark:active {
      background: ${({ theme }) => theme.colors.AccentMediumChampagne};
      outline-color: transparent;
      border-color: transparent;
      color: ${({ theme }) => theme.colors.AccentNapplesYellow};
    }
    &[disabled] {
      color: ${({ theme }) => theme.colors.SurfaceRomanSilver30};
    }
  }
`;
